import { useState, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { useNavigate } from 'react-router-dom';
import { loginRequest } from '../authConfig';
import { useOTPAuth } from '../auth/providers/OTPAuthProvider';
import { tokenService } from '../auth/services/tokenService';

interface FetchOptions {
  method?: string;
  headers?: Record<string, string>;
  body?: string | FormData;
  timeout?: number; // timeout in milliseconds
}

interface AuthenticatedFetchState {
  loading: boolean;
  error: string | null;
}

interface AuthenticatedFetchReturn {
  loading: boolean;
  error: string | null;
  authenticatedFetch: (url: string, options?: FetchOptions) => Promise<Response | null>;
}

/**
 * Custom hook for making authenticated API calls with Azure AD tokens
 * Handles token acquisition, renewal, and error scenarios
 */
export function useAuthenticatedFetch(): AuthenticatedFetchReturn {
  const { instance, accounts, inProgress } = useMsal();
  const { isAuthenticated: isOTPAuthenticated, user: otpUser } = useOTPAuth();
  const navigate = useNavigate();
  const [state, setState] = useState<AuthenticatedFetchState>({
    loading: false,
    error: null,
  });

  const authenticatedFetch = useCallback(
    async (url: string, options: FetchOptions = {}): Promise<Response | null> => {
      setState({ loading: true, error: null });

      try {
        let accessToken: string | null = null;

        // Check for OTP authentication first
        if (isOTPAuthenticated) {
          accessToken = tokenService.getAccessToken();
          
          if (!accessToken || tokenService.isTokenExpired(accessToken)) {
            console.warn('OTP token expired or missing');
            setState({ loading: false, error: 'Authentication token expired. Please sign in again.' });
            return null;
          }
          
          console.log('Using OTP authentication token');
        } 
        // Check for Microsoft authentication
        else if (accounts.length > 0) {
          // Prevent multiple simultaneous token acquisitions
          if (inProgress !== 'none') {
            console.warn('Token acquisition already in progress, waiting...');
            return null;
          }

          const account = accounts[0];
          console.log('Acquiring token for account:', account.username);

          // Prepare request for token acquisition
          const request = {
            ...loginRequest,
            account: account,
          };

          try {
            // Try to acquire token silently
            console.log('Attempting silent token acquisition...');
            const response = await instance.acquireTokenSilent(request);
            accessToken = response.accessToken;
            console.log('Token acquired silently');
          } catch (error: any) {
            console.warn('Silent token acquisition failed:', error);
            
            // Check if this is a consent_required or interaction_required error
            const errorCode = error?.errorCode;
            const errorMessage = error?.errorMessage || '';
            
            // Check for authorization errors indicating AAD app needs approval
            if (errorCode === 'consent_required' || 
                errorCode === 'interaction_required' ||
                errorCode === 'admin_consent_required' ||
                errorCode === 'login_required' ||
                errorMessage.includes('AADSTS65001') || // Consent required
                errorMessage.includes('AADSTS50076') || // MFA required but app not approved
                errorMessage.includes('AADSTS90094') || // Admin consent required
                errorMessage.includes('AADSTS90095') || // Admin has not consented
                errorMessage.includes('AADSTS65004') || // User declined consent
                errorMessage.includes('AADSTS700003') || // Device auth required but not approved
                errorMessage.includes('AADSTS7000112') || // Application disabled
                errorMessage.includes('AADSTS700016')) { // Application not found in directory
              
              console.warn('Authorization required: AAD application needs admin approval');
              setState({ 
                loading: false, 
                error: 'Authorization required. Redirecting...' 
              });
              
              // Redirect to authorization needed page
              navigate('/authorization-needed');
              return null;
            }
            
            // For other errors, treat as authentication failure
            console.error('Token acquisition failed:', error);
            setState({ 
              loading: false, 
              error: 'Authentication token expired. Please refresh the page.' 
            });
            return null;
          }
        }
        // No authentication found
        else {
          console.warn('No authenticated accounts found (neither MSAL nor OTP)');
          setState({ loading: false, error: 'Not authenticated' });
          return null;
        }

        // Ensure we have a token
        if (!accessToken) {
          console.error('Failed to obtain access token');
          setState({ loading: false, error: 'Failed to obtain authentication token' });
          return null;
        }

        // Make authenticated API call with timeout
        const controller = new AbortController();
        const timeoutMs = options.timeout || 10000; // Default to 10 seconds if not specified
        const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

        // Don't set Content-Type for FormData - let browser set it with boundary
        const isFormData = options.body instanceof FormData;
        const headers: Record<string, string> = {
          'Authorization': `Bearer ${accessToken}`,
          ...options.headers,
        };
        
        // Only add Content-Type for non-FormData requests
        if (!isFormData && !options.headers?.['Content-Type']) {
          headers['Content-Type'] = 'application/json';
        }

        console.log('Making authenticated request to:', url);
        
        try {
          const response = await fetch(url, {
            ...options,
            headers,
            signal: controller.signal,
          });
          
          clearTimeout(timeoutId);
          setState({ loading: false, error: null });
          return response;
        } catch (fetchError: any) {
          clearTimeout(timeoutId);
          // Check if it was a timeout error
          if (fetchError.name === 'AbortError') {
            console.error('Request timed out after', timeoutMs, 'ms');
            throw new Error(`Request timed out after ${timeoutMs / 1000} seconds`);
          }
          throw fetchError;
        }

      } catch (error) {
        console.error('Authenticated fetch error:', error);
        setState({ 
          loading: false, 
          error: error instanceof Error ? error.message : 'Network request failed' 
        });
        return null;
      }
    },
    [instance, accounts, inProgress, navigate, isOTPAuthenticated]
  );

  return {
    loading: state.loading,
    error: state.error,
    authenticatedFetch,
  };
}